import { useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { AnonymityState } from "../../api/types";

interface AnonymityChoiceModalProps {
  open: boolean;
  seasonID: string;
  seasonName: string;
  onChosen: (state: AnonymityState) => void;
}

// AnonymityChoiceModal blocks the gumballs page until the user picks.
// Lock-once-per-season: the only way out is to make a choice.
// Intentionally has no Close/Cancel — the user cannot proceed without it.
export function AnonymityChoiceModal({
  open,
  seasonID,
  seasonName,
  onChosen
}: AnonymityChoiceModalProps) {
  const [submitting, setSubmitting] = useState<"anon" | "visible" | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return null;
  }

  async function choose(anonymous: boolean) {
    if (submitting !== null) {
      return;
    }
    setSubmitting(anonymous ? "anon" : "visible");
    setError(null);
    try {
      const state = await apiClient.setAnonymity(seasonID, anonymous);
      onChosen(state);
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setSubmitting(null);
    }
  }

  return (
    <div className="modal-overlay" role="presentation">
      <div
        className="modal anonymity-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="anonymity-modal-title"
      >
        <header className="modal__header">
          <h2 id="anonymity-modal-title">One choice for {seasonName}</h2>
        </header>
        <div className="modal__body">
          <p>
            Before you can give or receive gumballs this season, pick how you'd like to show up.
          </p>
          <p className="anonymity-modal__warning">
            <strong>This choice is locked for the season.</strong> You can't change it later.
          </p>
          <div className="anonymity-modal__options">
            <button
              type="button"
              className="anonymity-modal__option"
              onClick={() => choose(false)}
              disabled={submitting !== null}
            >
              <h3>Use my name</h3>
              <p>
                Recipients see who sent each gumball. You see who sends to you.
                Notes are visible.
              </p>
              {submitting === "visible" && (
                <span className="anonymity-modal__busy">Saving…</span>
              )}
            </button>
            <button
              type="button"
              className="anonymity-modal__option anonymity-modal__option--anon"
              onClick={() => choose(true)}
              disabled={submitting !== null}
            >
              <h3>Stay anonymous</h3>
              <p>
                Recipients see "Anonymous" on gumballs you send. Senders are
                hidden from your incoming feed too. Notes are turned off.
              </p>
              <small>
                Heads up: in a small team, others may still infer that
                "Anonymous" is you.
              </small>
              {submitting === "anon" && (
                <span className="anonymity-modal__busy">Saving…</span>
              )}
            </button>
          </div>
          {error !== null && <p className="form-row__error">{error}</p>}
        </div>
      </div>
    </div>
  );
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.code === "anonymity_already_set") {
      return "You've already made your choice for this season.";
    }
    if (caught.code === "no_active_season" || caught.code === "season_not_active") {
      return "No active season — try again once your admin opens one.";
    }
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Couldn't save your choice.";
}
